import React, { useState } from "react";
import { Volume2, VolumeX, Play, Square } from "lucide-react";
import { useTTS } from "../hooks/useTTS";

/**
 * VoiceCoachPreview — small, self-contained "try the voice coach" card.
 *
 * Lets a user (or admin) hear a one-line affirming phrase before
 * diving into the AR mirror. Reuses the same `useTTS` hook the mirror
 * uses, so toggling the preference here flips it in the mirror too.
 *
 * If the browser does not support `SpeechSynthesis`, the whole card
 * collapses to nothing — no graceful-degradation noise needed.
 */
const SAMPLE = "Hello, I'm your TruBeauté coach. Let's celebrate you.";

const VoiceCoachPreview = ({ phrase = SAMPLE, className = "" }) => {
  const { enabled, toggle, speak, stop, supported, voice, setVoice } = useTTS();
  const [speaking, setSpeaking] = useState(false);

  if (!supported) return null;

  // The 4 voices that map best to our affirming, celebratory tone.
  // "Trouvé" (literally "found" in French — pairs with Beauté) is our
  // default voice persona: your personal drag mom, warm and ready to
  // coach you through every step. Backed by OpenAI TTS `nova` under the
  // hood, but users only ever see "Trouvé" so the coach has a name and
  // a personality of her own.
  const VOICES = [
    { id: "nova", label: "Trouvé", subtitle: "your personal drag mom · warm + ready" },
    { id: "shimmer", label: "Shimmer", subtitle: "soft + intimate" },
    { id: "sage", label: "Sage", subtitle: "calm + grounded" },
    { id: "fable", label: "Fable", subtitle: "British storyteller" },
  ];

  const onPlay = () => {
    if (speaking) {
      stop();
      setSpeaking(false);
      return;
    }
    // Force-speak even if `enabled` is currently OFF — the user is
    // explicitly asking for a preview. We do this by temporarily
    // flipping `enabled`, then queuing the speak() call after the
    // resulting render so the closure captures `enabled=true`.
    if (!enabled) toggle();
    setSpeaking(true);
    setTimeout(() => speak(phrase), 0);

    // Heuristic timeout to release the "speaking" state in case the
    // backend stalls or audio onended fires somewhere we can't reach.
    // Nova at speed 1.0 averages ~140 wpm, so ~430ms / word + 1s slack.
    const wc = phrase.split(/\s+/).length;
    const ms = Math.max(2000, wc * 430 + 1000);
    setTimeout(() => setSpeaking(false), ms);
  };

  return (
    <div
      className={`bg-white/80 backdrop-blur-xl border border-[#EADDD7] rounded-3xl p-6 soft-shadow ${className}`}
      data-testid="voice-coach-preview-card"
    >
      <div className="flex items-start gap-4 flex-col sm:flex-row sm:items-center justify-between">
        <div className="flex items-start gap-4 flex-1">
          <div className="h-10 w-10 rounded-full bg-[#F2E8E9] flex items-center justify-center shrink-0 mt-0.5">
            {enabled ? (
              <Volume2 className="h-5 w-5 text-[#9B6A6C]" />
            ) : (
              <VolumeX className="h-5 w-5 text-[#9B6A6C]" />
            )}
          </div>
          <div>
            <h3 className="text-lg font-medium text-[#2C2123] mb-1">
              Voice coach
            </h3>
            <p className="text-sm text-[#5C4B51] leading-relaxed max-w-md mb-3">
              In Mirror Mode, I can read each step out loud so your hands stay
              free. Tap play to hear how it sounds — toggle to enable it for
              your next routine.
            </p>
            {/* Voice picker — only the 4 most TruBeauté-fitting voices.
                Selecting a voice cancels any in-flight playback so the
                user immediately hears the new one on next play. */}
            <div
              className="flex flex-wrap gap-2"
              role="radiogroup"
              aria-label="Choose voice"
              data-testid="voice-coach-voice-picker"
            >
              {VOICES.map((v) => {
                const selected = voice === v.id;
                return (
                  <button
                    key={v.id}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    onClick={() => {
                      setVoice(v.id);
                      setSpeaking(false);
                    }}
                    className={`text-xs rounded-full px-3 py-1.5 border transition-all ${
                      selected
                        ? "bg-[#9B6A6C] text-white border-[#9B6A6C]"
                        : "bg-white border-[#EADDD7] text-[#5C4B51] hover:border-[#D5A8A4]"
                    }`}
                    data-testid={`voice-coach-voice-${v.id}`}
                  >
                    <span className="font-medium">{v.label}</span>
                    <span
                      className={`ml-1.5 text-[10px] uppercase tracking-[0.12em] ${
                        selected ? "text-white/80" : "text-[#8A7D82]"
                      }`}
                    >
                      {v.subtitle}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 self-stretch sm:self-auto sm:flex-row flex-row sm:items-center">
          <button
            onClick={onPlay}
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm transition-all whitespace-nowrap ${
              speaking
                ? "bg-[#9B6A6C] text-white hover:bg-[#805557]"
                : "bg-[#FAF6F2] hover:bg-[#F2E8E9] border border-[#D5A8A4] text-[#5D4050]"
            }`}
            data-testid="voice-coach-preview-play-btn"
            aria-label={speaking ? "Stop preview" : "Play voice coach preview"}
          >
            {speaking ? (
              <>
                <Square className="h-4 w-4" /> Stop
              </>
            ) : (
              <>
                <Play className="h-4 w-4" /> Try a phrase
              </>
            )}
          </button>
          <button
            onClick={() => {
              toggle();
              if (speaking) {
                stop();
                setSpeaking(false);
              }
            }}
            className={`relative h-9 w-16 rounded-full transition-colors shrink-0 ${
              enabled ? "bg-[#D5A8A4]" : "bg-[#EADDD7]"
            }`}
            data-testid="voice-coach-preview-toggle"
            aria-pressed={enabled}
            aria-label={
              enabled ? "Turn off voice coach for Mirror Mode" : "Turn on voice coach for Mirror Mode"
            }
          >
            <span
              className={`absolute top-1 h-7 w-7 rounded-full bg-white shadow transition-all ${
                enabled ? "left-8" : "left-1"
              }`}
            />
          </button>
        </div>
      </div>
    </div>
  );
};

export default VoiceCoachPreview;
